import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { defaultImage } from '../../settings/config';
import { ShoppingCart, Pencil, Trash2, X, User, Layout } from 'lucide-react';

interface CardsCardProps {
  card: any;
  deleteAction: (id: string) => void;
}

function CardsCard({ card, deleteAction }: CardsCardProps) {
  const [showConfirm, setShowConfirm] = useState(false);

  const purchase = (event: React.MouseEvent) => {
    event.preventDefault();
    if (card.purchase_url) {
      window.open(card.purchase_url, '_blank');
    }
  };

  const confirmDelete = (event: React.MouseEvent) => {
    event.preventDefault();
    setShowConfirm(true);
  };

  const handleDelete = () => {
    deleteAction(card.id);
    setShowConfirm(false);
  };

  return (
    <>
      <div className="group relative rounded-2xl overflow-hidden bg-card border border-border/40 shadow-soft-sm hover:shadow-soft-lg transition-all duration-300 hover:-translate-y-1.5">
        {/* Accent bar */}
        <div className="h-1.5 bg-gradient-to-r from-accent via-primary to-secondary" />

        {/* Card preview image — trading card aesthetic */}
        <Link to={`/cards/${card.id}`} className="block relative overflow-hidden no-underline">
          <img
            alt={`${card.title} preview`}
            className="w-full h-52 object-cover transition-transform duration-500 group-hover:scale-105"
            src={card.preview_image ? card.preview_image : defaultImage}
          />
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

          {/* Title overlay at bottom */}
          <div className="absolute bottom-0 left-0 right-0 p-4">
            <h3 className="font-bold text-lg text-white drop-shadow-md">
              {card.title}
            </h3>
          </div>

          {/* Purchase badge */}
          {card.purchase_url && (
            <div className="absolute top-3 right-3">
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-success/90 text-white text-xs font-medium backdrop-blur-sm shadow-soft-sm">
                <ShoppingCart className="w-3 h-3" />
                Available
              </span>
            </div>
          )}
        </Link>

        {/* Metadata chips */}
        <div className="px-4 pt-3 pb-2 flex flex-wrap items-center gap-1.5">
          {card.child_name && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-primary/10 dark:bg-primary/15 text-primary text-xs font-medium">
              <User className="w-3 h-3" />
              {card.child_name}
            </span>
          )}
          {card.template_name && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-accent/10 dark:bg-accent/15 text-accent text-xs font-medium">
              <Layout className="w-3 h-3" />
              {card.template_name}
            </span>
          )}
        </div>

        {/* Action buttons — icon pills */}
        <div className="flex items-center justify-center gap-1.5 px-4 pb-4 pt-1">
          <button
            onClick={purchase}
            disabled={!card.purchase_url}
            className="p-2.5 rounded-xl text-muted-foreground hover:text-success hover:bg-success/10 transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-muted-foreground"
            title="Purchase"
            aria-label="Purchase card"
          >
            <ShoppingCart className="w-4 h-4" />
          </button>
          <Link
            to={`/cards/${card.id}/edit`}
            className="p-2.5 rounded-xl text-muted-foreground hover:text-accent hover:bg-accent/10 transition-all duration-200 no-underline"
            title="Edit"
            aria-label="Edit card"
          >
            <Pencil className="w-4 h-4" />
          </Link>
          <button
            onClick={confirmDelete}
            className="p-2.5 rounded-xl text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all duration-200"
            title="Delete"
            aria-label="Delete card"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Delete confirmation modal */}
      <AnimatePresence>
        {showConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
            onClick={() => setShowConfirm(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-card rounded-2xl p-6 mx-4 max-w-sm w-full shadow-soft-xl border border-border"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-10 h-10 rounded-xl bg-destructive/10 flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-destructive" />
                </div>
                <button
                  onClick={() => setShowConfirm(false)}
                  className="p-1 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <h3 className="font-semibold text-lg text-card-foreground mb-2">Delete this card?</h3>
              <p className="text-sm text-muted-foreground mb-6">
                If you've already printed this card, the QR code will no longer work as expected.
              </p>
              <div className="flex gap-3 justify-end">
                <button onClick={() => setShowConfirm(false)} className="btn-light px-4 py-2.5 rounded-xl">
                  Cancel
                </button>
                <button onClick={handleDelete} className="btn-danger px-4 py-2.5 rounded-xl">
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default CardsCard;
